import type { IOwnAccountsTransfer } from "@/modules/transaction/common/types";
import { Amount } from "@/shared/components";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  transfer: IOwnAccountsTransfer;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const OwnAccountTransferDetailRows = ({
  transfer,
  scrollbarContainerRef
}: Props) => {
  const { t } = useTranslation();

  return (
    <div>
      <div>
        <CollapseGroup
          className="detail-collapse"
          items={[
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.ownAccounts.fromAccount")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.debitAccount?.number as string}
                        />
                        <Text size="medium" weight="bold">{transfer.debitAccount?.number}</Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.ownAccounts.toAccount")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.creditAccount?.number as string}
                        />
                        <Text size="medium" weight="bold">{transfer.creditAccount?.number}</Text>
                      </div>
                    </div>
                  </div>
                </>
              ),
              value: "account_details",
              isOpen: true,
              title: t("transfer.accountDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.amount")}
                      </Text>
                      <Amount
                        currency={transfer.currency}
                        amount={transfer.amount as number}
                      />
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.purpose")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="account-transfer-purpose-tooltip"
                        text={transfer.purpose}
                      />
                      <Text
                        id="account-transfer-purpose-tooltip"
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {transfer.purpose}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "transfer_details",
              isOpen: true,
              title: t("transfer.transferDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.transactionNumber")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.transactionNumber}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.transactionNumber}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text type="secondary" size="medium" weight="semibold">
                        {t("general.date")}
                      </Text>
                      <Text size="medium" weight="bold">
                        {transfer.scheduleDate}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "other_details",
              isOpen: true,
              title: t("general.otherDetails")
            },
          ]}
        />
      </div>
    </div>
  );
};
